import React, { useState } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdFileDownload, MdPrint, MdSearch } from 'react-icons/md';
import '../master/MasterData.css';

interface GLTransaction {
  transactionDate: string;
  documentType: string;
  documentNo: string;
  description: string;
  reference: string;
  debit: number;
  credit: number;
  balance: number;
}

interface AccountSummary {
  accountCode: string;
  accountName: string;
  accountType: string;
  openingBalance: number;
  totalDebits: number;
  totalCredits: number;
  closingBalance: number;
}

const GeneralLedgerReports: React.FC = () => {
  const [selectedAccount, setSelectedAccount] = useState('1001');
  const [dateFrom, setDateFrom] = useState('2025-01-01');
  const [dateTo, setDateTo] = useState('2025-03-31');
  const [searchTerm, setSearchTerm] = useState('');

  // Sample Account List
  const accounts = [
    { code: '1001', name: 'Cash on Hand', type: 'Asset' },
    { code: '1002', name: 'Cash in Bank - BPI', type: 'Asset' },
    { code: '1101', name: 'Accounts Receivable', type: 'Asset' },
    { code: '1201', name: 'Inventory - Raw Materials', type: 'Asset' },
    { code: '1202', name: 'Inventory - Finished Goods', type: 'Asset' },
    { code: '2001', name: 'Accounts Payable', type: 'Liability' },
    { code: '3001', name: 'Capital Stock', type: 'Equity' },
    { code: '4001', name: 'Sales Revenue', type: 'Revenue' },
    { code: '5001', name: 'Cost of Goods Sold', type: 'Expense' },
    { code: '6001', name: 'Salaries and Wages', type: 'Expense' }
  ];

  // Sample GL Transactions for selected account
  const glTransactions: GLTransaction[] = [
    {
      transactionDate: '2025-01-01',
      documentType: 'Opening Balance',
      documentNo: 'OB-2025',
      description: 'Beginning balance for 2025',
      reference: '',
      debit: 0,
      credit: 0,
      balance: 500000
    },
    {
      transactionDate: '2025-01-05',
      documentType: 'Sales Invoice',
      documentNo: 'SI-2025-0001',
      description: 'Payment received from ABC Furniture Corp.',
      reference: 'CUST001',
      debit: 0,
      credit: 0,
      balance: 500000
    },
    {
      transactionDate: '2025-01-05',
      documentType: 'Bank Receipt',
      documentNo: 'BR-2025-0001',
      description: 'Cash received from sales',
      reference: 'SI-2025-0001',
      debit: 500000,
      credit: 0,
      balance: 1000000
    },
    {
      transactionDate: '2025-01-10',
      documentType: 'Purchase Invoice',
      documentNo: 'PI-2025-0005',
      description: 'Payment for raw materials',
      reference: 'VEND002',
      debit: 0,
      credit: 250000,
      balance: 750000
    },
    {
      transactionDate: '2025-01-15',
      documentType: 'Journal Entry',
      documentNo: 'JE-2025-0012',
      description: 'Salary payment for January period 1',
      reference: '',
      debit: 0,
      credit: 1437500,
      balance: -687500
    },
    {
      transactionDate: '2025-02-01',
      documentType: 'Bank Receipt',
      documentNo: 'BR-2025-0045',
      description: 'Collection from customers',
      reference: 'Multiple',
      debit: 2250000,
      credit: 0,
      balance: 1562500
    },
    {
      transactionDate: '2025-02-10',
      documentType: 'Bank Payment',
      documentNo: 'BP-2025-0028',
      description: 'Payment to suppliers',
      reference: 'Multiple',
      debit: 0,
      credit: 875000,
      balance: 687500
    },
    {
      transactionDate: '2025-03-01',
      documentType: 'Bank Receipt',
      documentNo: 'BR-2025-0092',
      description: 'Customer payments received',
      reference: 'Multiple',
      debit: 1850000,
      credit: 0,
      balance: 2537500
    },
    {
      transactionDate: '2025-03-15',
      documentType: 'Bank Payment',
      documentNo: 'BP-2025-0055',
      description: 'Vendor payments and expenses',
      reference: 'Multiple',
      debit: 0,
      credit: 1125000,
      balance: 1412500
    },
    {
      transactionDate: '2025-03-31',
      documentType: 'Journal Entry',
      documentNo: 'JE-2025-0089',
      description: 'Month-end adjustments',
      reference: '',
      debit: 125000,
      credit: 0,
      balance: 1537500
    }
  ];

  const filteredTransactions = glTransactions.filter(tx =>
    tx.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.documentNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tx.reference.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedAccountInfo = accounts.find(acc => acc.code === selectedAccount);

  const openingBalance = glTransactions[0]?.balance || 0;
  const totalDebits = filteredTransactions.reduce((sum, tx) => sum + tx.debit, 0);
  const totalCredits = filteredTransactions.reduce((sum, tx) => sum + tx.credit, 0);
  const closingBalance = filteredTransactions[filteredTransactions.length - 1]?.balance || 0;

  const accountSummary: AccountSummary = {
    accountCode: selectedAccount,
    accountName: selectedAccountInfo?.name || '',
    accountType: selectedAccountInfo?.type || '',
    openingBalance,
    totalDebits,
    totalCredits,
    closingBalance
  };

  const handleExport = () => {
    alert('Export to Excel functionality would be implemented here');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>General Ledger Reports</h2>
            <p className="subtitle">Detailed transaction history by account</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn btn-secondary" onClick={handlePrint}>
              <MdPrint /> Print
            </button>
            <button className="btn btn-primary" onClick={handleExport}>
              <MdFileDownload /> Export to Excel
            </button>
          </div>
        </div>

        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">💼</div>
            <div className="summary-content">
              <p className="summary-label">Opening Balance</p>
              <p className="summary-value">₱{accountSummary.openingBalance.toLocaleString()}</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📈</div>
            <div className="summary-content">
              <p className="summary-label">Total Debits</p>
              <p className="summary-value" style={{ color: '#28a745' }}>
                ₱{accountSummary.totalDebits.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">📉</div>
            <div className="summary-content">
              <p className="summary-label">Total Credits</p>
              <p className="summary-value" style={{ color: '#dc3545' }}>
                ₱{accountSummary.totalCredits.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💰</div>
            <div className="summary-content">
              <p className="summary-label">Closing Balance</p>
              <p className="summary-value" style={{ color: '#1a237e', fontWeight: 'bold' }}>
                ₱{accountSummary.closingBalance.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div style={{
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ margin: '0 0 10px 0', color: '#1a237e' }}>Account Information</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
              <div>
                <strong>Account Code:</strong> {accountSummary.accountCode}
              </div>
              <div>
                <strong>Account Name:</strong> {accountSummary.accountName}
              </div>
              <div>
                <strong>Account Type:</strong> {accountSummary.accountType}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ minWidth: '300px' }}>
              <label>Select Account</label>
              <select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                style={{ width: '100%' }}
              >
                {accounts.map(acc => (
                  <option key={acc.code} value={acc.code}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ minWidth: '180px' }}>
              <label>Date From</label>
              <input
                type="date"
                  lang="en-US"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ minWidth: '180px' }}>
              <label>Date To</label>
              <input
                type="date"
                  lang="en-US"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="search-box" style={{ flex: 1 }}>
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Document Type</th>
                  <th>Document No.</th>
                  <th>Description</th>
                  <th>Reference</th>
                  <th>Debit</th>
                  <th>Credit</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((tx, index) => (
                  <tr key={index} style={{
                    backgroundColor: tx.documentType === 'Opening Balance' ? '#e3f2fd' : 'transparent'
                  }}>
                    <td>{new Date(tx.transactionDate).toLocaleDateString('en-US')}</td>
                    <td>{tx.documentType}</td>
                    <td className="code">{tx.documentNo}</td>
                    <td className="name">{tx.description}</td>
                    <td>{tx.reference}</td>
                    <td className="amount" style={{ color: tx.debit > 0 ? '#28a745' : '#999', fontWeight: tx.debit > 0 ? 'bold' : 'normal' }}>
                      {tx.debit > 0 ? `₱${tx.debit.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount" style={{ color: tx.credit > 0 ? '#dc3545' : '#999', fontWeight: tx.credit > 0 ? 'bold' : 'normal' }}>
                      {tx.credit > 0 ? `₱${tx.credit.toLocaleString()}` : '-'}
                    </td>
                    <td className="amount" style={{
                      fontWeight: 'bold',
                      color: tx.balance >= 0 ? '#1a237e' : '#dc3545'
                    }}>
                      ₱{tx.balance.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                  <td colSpan={5} style={{ textAlign: 'right' }}>TOTAL:</td>
                  <td className="amount" style={{ color: '#28a745' }}>₱{totalDebits.toLocaleString()}</td>
                  <td className="amount" style={{ color: '#dc3545' }}>₱{totalCredits.toLocaleString()}</td>
                  <td className="amount" style={{ color: '#1a237e' }}>
                    ₱{closingBalance.toLocaleString()}
                  </td>
                </tr>
              </tfoot>
            </table>

            {filteredTransactions.length === 0 && (
              <div className="no-data">
                <p>No transactions found for the selected criteria</p>
              </div>
            )}
          </div>

          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Opening Balance</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1a237e' }}>
                  ₱{openingBalance.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Debits (+)</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#28a745' }}>
                  ₱{totalDebits.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Total Credits (-)</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                  ₱{totalCredits.toLocaleString()}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Closing Balance</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1a237e' }}>
                  ₱{closingBalance.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default GeneralLedgerReports;
